import { useLingui } from "@lingui/react/macro"
import { Button, TextInput } from "@mantine/core"
import { IconCalendar, IconX } from "@tabler/icons-react"
import { SEARCH_CHANNELS } from "../../lib/types/search"
import { CHANNEL_META, ChannelLabel } from "../channel-meta"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * Search refinement bar — channel chips, an optional speaker filter, and an
 * inclusive date range. Pure presentation: every change bubbles to the host,
 * which owns the URL search-param state. Channel chips are semantic-toned
 * toggles (data-tone), not literal colours (theming rule #6).
 */
export const SearchFilters = ({
  channels,
  onToggleChannel,
  speaker,
  onSpeakerChange,
  from,
  to,
  onFromChange,
  onToChange,
  onClearAll,
  hasActiveFilters,
}: IProps): React.JSX.Element => {
  const { t } = useLingui()

  return (
    <div className={classes.wrap}>
      <fieldset className={classes.chips} aria-label={t`Filter by content type`}>
        {SEARCH_CHANNELS.map((channel) => {
          const { tone, Icon } = CHANNEL_META[channel]
          const active = channels.includes(channel)
          return (
            <button
              key={channel}
              type="button"
              className={classes.chip}
              data-tone={tone}
              data-active={active || undefined}
              aria-pressed={active}
              onClick={() => onToggleChannel(channel)}
            >
              <Icon size={13} />
              <ChannelLabel sourceChannel={channel} />
            </button>
          )
        })}
      </fieldset>

      <div className={classes.controls}>
        <TextInput
          className={classes.speaker}
          size="sm"
          value={speaker}
          onChange={(e) => onSpeakerChange(e.currentTarget.value)}
          placeholder={t`Speaker`}
          aria-label={t`Filter by speaker`}
        />
        <TextInput
          className={classes.date}
          size="sm"
          type="date"
          value={from}
          onChange={(e) => onFromChange(e.currentTarget.value)}
          leftSection={<IconCalendar size={14} />}
          aria-label={t`From date`}
        />
        <TextInput
          className={classes.date}
          size="sm"
          type="date"
          value={to}
          onChange={(e) => onToChange(e.currentTarget.value)}
          leftSection={<IconCalendar size={14} />}
          aria-label={t`To date`}
        />
        {hasActiveFilters && (
          <Button variant="subtle" color="gray" size="sm" leftSection={<IconX size={14} />} onClick={onClearAll}>
            {t`Clear all`}
          </Button>
        )}
      </div>
    </div>
  )
}
